/* sc-shopify-cart.js
 * Cart integration for Sophie Conran Shopify theme.
 *
 * Two modes:
 *
 * 1. SOFA (configured products):
 *    Configurator → Vercel pricing API → Shopify Draft Order → Checkout.
 *    The customer never sees a traditional cart for sofas. They configure,
 *    see a live price from Vercel, and "Add to basket" takes them to a
 *    Shopify checkout with the correct price pre-set.
 *
 * 2. STANDARD (bed linen, Portmeirion, etc.):
 *    Normal Shopify /cart/add.js → /cart page → Checkout.
 *    Uses variant prices from Shopify product data.
 *
 * ── CONFIGURATION ──
 * Set SC.cart.config before this script loads (or edit the defaults below).
 */
(function() {
  'use strict';

  window.SC = window.SC || {};
  SC.cart = SC.cart || {};

  /* ── Config: set these when the Vercel API contract is confirmed ── */
  SC.cart.config = SC.cart.config || {
    /* Vercel pricing API endpoint.
       Request: POST with JSON body (see SC.cart.getPriceFromVercel).
       Response: { price: 5895, currency: 'GBP', breakdown: {...} } */
    vercelPricingUrl: '/api/pricing',  /* ← UPDATE WITH REAL URL */

    /* Vercel draft order endpoint.
       Creates a Shopify draft order and returns a checkout URL.
       Request: POST with JSON body (see SC.cart.createDraftOrder).
       Response: { checkoutUrl: 'https://...', draftOrderId: '...' } */
    vercelDraftOrderUrl: '/api/draft-order',  /* ← UPDATE WITH REAL URL */

    /* Shopify store domain (for standard cart API) */
    shopDomain: window.location.hostname,

    /* Currency */
    currency: 'GBP'
  };

  /* ════════════════════════════════════════════════
     1. VERCEL PRICING API
     Called by the configurator to get live prices
     as the customer makes selections.
     ════════════════════════════════════════════════ */

  /**
   * Get the configured price from the Vercel pricing API.
   *
   * @param {Object} config - The sofa configuration
   * @param {string} config.range     - e.g. 'coco'
   * @param {string} config.size      - e.g. '2.5-seater'
   * @param {string} config.fabric    - e.g. 'cool-coconut'
   * @param {string} config.cushion   - e.g. 'knife-edge'
   * @param {string} config.cover     - e.g. 'loose'
   * @param {string} [config.piping]  - e.g. 'self' or 'contrast'
   * @returns {Promise<{price: number, currency: string, breakdown: Object}>}
   *
   * ── NOTE FOR ANKIT/RUDR ──
   * Adjust the request body below to match your actual API contract.
   * The response must include at minimum: { price: number }
   * where price is in pence/cents (integer) or pounds (decimal).
   */
  SC.cart.getPriceFromVercel = async function(config) {
    try {
      var response = await fetch(SC.cart.config.vercelPricingUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          /* ── Adjust these field names to match the Vercel API ── */
          range: config.range,
          size: config.size,
          fabric: config.fabric,
          cushionStyle: config.cushion,
          coverType: config.cover,
          piping: config.piping || 'self'
        })
      });

      if (!response.ok) {
        throw new Error('Pricing API returned ' + response.status);
      }

      var data = await response.json();

      return {
        price: data.price,        /* ← Adjust if field name differs */
        currency: data.currency || SC.cart.config.currency,
        breakdown: data.breakdown || data  /* full response for debugging */
      };
    } catch (err) {
      console.error('[SC.cart] Pricing API error:', err);
      /* Fall back to the prototype's local price calculation */
      return { price: null, currency: SC.cart.config.currency, error: err.message };
    }
  };

  /* ════════════════════════════════════════════════
     2. DRAFT ORDER CREATION (sofas)
     Called when the customer clicks "Add to basket"
     on a configured sofa.
     ════════════════════════════════════════════════ */

  /**
   * Create a Shopify draft order via Vercel and redirect to checkout.
   *
   * @param {Object} config - The full sofa configuration
   * @param {string} config.range
   * @param {string} config.size
   * @param {string} config.fabric
   * @param {string} config.fabricName    - human-readable fabric name
   * @param {string} config.cushion
   * @param {string} config.cover
   * @param {string} [config.piping]
   * @param {number} config.price         - from the pricing API
   * @param {string} config.productTitle  - e.g. 'Coco 2.5-seater'
   * @param {string} [config.variantId]   - Shopify variant ID for the size
   *
   * ── NOTE FOR ANKIT/RUDR ──
   * The Vercel endpoint needs to:
   * 1. Receive the configuration and price
   * 2. Create a Shopify Draft Order via the Admin API
   * 3. Return { checkoutUrl: '...' }
   *
   * The Draft Order should include:
   * - Line item: the product variant (by size) at the calculated price
   * - Line item properties: fabric, cushion, cover, piping (for workshop)
   * - Note: the full configuration as a JSON string (for records)
   */
  SC.cart.createDraftOrder = async function(config) {
    try {
      /* Show loading state on the button */
      SC.cart._setButtonLoading(true);

      var response = await fetch(SC.cart.config.vercelDraftOrderUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          /* ── Adjust these field names to match the Vercel API ── */
          range: config.range,
          size: config.size,
          fabric: config.fabric,
          fabricName: config.fabricName,
          cushionStyle: config.cushion,
          coverType: config.cover,
          piping: config.piping || 'self',
          price: config.price,
          currency: SC.cart.config.currency,
          productTitle: config.productTitle,
          variantId: config.variantId || null,

          /* Line item properties (visible to customer and workshop) */
          properties: {
            'Range': config.range,
            'Size': config.size,
            'Fabric': config.fabricName || config.fabric,
            'Cushion style': config.cushion,
            'Cover': config.cover,
            'Piping': config.piping || 'Self'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Draft order API returned ' + response.status);
      }

      var data = await response.json();

      if (data.checkoutUrl) {
        /* Redirect to Shopify checkout */
        window.location.href = data.checkoutUrl;
      } else {
        throw new Error('No checkout URL in response');
      }

    } catch (err) {
      console.error('[SC.cart] Draft order error:', err);
      SC.cart._setButtonLoading(false);
      SC.cart._showError(
        'Something went wrong creating your order. ' +
        'Please try again, or contact us if the problem persists.'
      );
    }
  };

  /* ════════════════════════════════════════════════
     3. STANDARD SHOPIFY CART (non-sofa products)
     Used for bed linen, Portmeirion, etc.
     ════════════════════════════════════════════════ */

  /**
   * Add a standard product to the Shopify cart.
   *
   * @param {string|number} variantId - Shopify variant ID
   * @param {number} [quantity=1]
   * @param {Object} [properties] - Optional line item properties
   * @returns {Promise<Object>} - The cart response
   */
  SC.cart.addToCart = async function(variantId, quantity, properties) {
    quantity = quantity || 1;

    var body = {
      items: [{
        id: parseInt(variantId, 10),
        quantity: quantity
      }]
    };

    if (properties && Object.keys(properties).length > 0) {
      body.items[0].properties = properties;
    }

    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error('Cart API returned ' + response.status);
      }

      var data = await response.json();
      SC.cart._updateCartBadge();
      return data;

    } catch (err) {
      console.error('[SC.cart] Add to cart error:', err);
      throw err;
    }
  };

  /**
   * Get the current Shopify cart.
   */
  SC.cart.getCart = async function() {
    var response = await fetch('/cart.js', {
      headers: { 'Content-Type': 'application/json' }
    });
    return response.json();
  };

  /**
   * Update a cart item's quantity.
   */
  SC.cart.updateQuantity = async function(lineKey, quantity) {
    var response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    });
    var data = await response.json();
    SC.cart._updateCartBadge();
    return data;
  };

  /**
   * Remove a cart item.
   */
  SC.cart.removeItem = async function(lineKey) {
    return SC.cart.updateQuantity(lineKey, 0);
  };

  /* ════════════════════════════════════════════════
     4. UI HELPERS
     ════════════════════════════════════════════════ */

  /**
   * Update the cart badge count in the header.
   */
  SC.cart._updateCartBadge = async function() {
    try {
      var cart = await SC.cart.getCart();
      var badges = document.querySelectorAll('[data-sc-cart-badge]');
      badges.forEach(function(badge) {
        if (cart.item_count > 0) {
          badge.textContent = cart.item_count;
          badge.style.display = '';
        } else {
          badge.style.display = 'none';
        }
      });
    } catch (err) { /* silent */ }
  };

  /**
   * Set the "Add to basket" button to loading state.
   */
  SC.cart._setButtonLoading = function(loading) {
    var btns = document.querySelectorAll(
      '[data-sc-config-action="quote"], .sc-config-sticky-quote'
    );
    btns.forEach(function(btn) {
      if (loading) {
        btn.setAttribute('disabled', 'disabled');
        btn.dataset.scOrigText = btn.textContent;
        btn.textContent = 'Processing...';
        btn.style.opacity = '0.7';
      } else {
        btn.removeAttribute('disabled');
        if (btn.dataset.scOrigText) btn.textContent = btn.dataset.scOrigText;
        btn.style.opacity = '';
      }
    });
  };

  /**
   * Show an error message near the Add to basket button.
   */
  SC.cart._showError = function(message) {
    /* Remove any existing error */
    var existing = document.querySelector('.sc-cart-error');
    if (existing) existing.remove();

    var el = document.createElement('div');
    el.className = 'sc-cart-error';
    el.textContent = message;
    el.style.cssText = 'background:#fef2f2;border:1px solid #fca5a5;color:#b91c1c;' +
      'padding:10px 14px;border-radius:3px;font-size:13px;margin:12px 0;';

    var target = document.querySelector('.sc-config-summary-actions') ||
                 document.querySelector('.sc-product-actions');
    if (target) target.parentNode.insertBefore(el, target.nextSibling);

    /* Auto-dismiss after 8 seconds */
    setTimeout(function() { el.remove(); }, 8000);
  };

  /* ════════════════════════════════════════════════
     5. WIRING: connect the configurator's Add to
        Basket button to the draft order flow.
     ════════════════════════════════════════════════ */

  /**
   * Call this from the configurator when "Add to basket" is clicked.
   * Reads the current configuration state and initiates the draft order.
   *
   * @param {Object} state - The configurator's internal state object
   */
  SC.cart.handleSofaAddToBasket = function(state) {
    if (!state || !state.rangeKey) {
      console.error('[SC.cart] No configurator state provided');
      return;
    }

    /* Build the configuration object from the state */
    var config = {
      range: state.rangeKey,
      size: state.selections.size || state.rules.defaultSize,
      fabric: state.selections.fabric || '',
      fabricName: state.selections.fabricName || state.selections.fabric || '',
      cushion: state.selections.cushion || state.rules.defaultCushion || '',
      cover: state.selections.cover || state.rules.defaultCover || '',
      piping: state.selections.piping || 'self',
      price: state.currentPrice || null,
      productTitle: (state.rules.rangeName || state.rangeKey) + ' ' +
                    (state.selections.size || state.rules.defaultSize),
      variantId: state.variantId || null
    };

    /* If we have a price from the local calculation, use it.
       In production, this would come from the Vercel pricing API
       (which is called during configuration, not at checkout time). */
    if (config.price) {
      SC.cart.createDraftOrder(config);
    } else {
      /* No price available — try getting one from Vercel first */
      SC.cart.getPriceFromVercel(config).then(function(result) {
        if (result.price) {
          config.price = result.price;
          SC.cart.createDraftOrder(config);
        } else {
          SC.cart._showError(
            'We could not calculate the price for this configuration. ' +
            'Please try again or contact us.'
          );
        }
      });
    }
  };

  /* ════════════════════════════════════════════════
     6. INITIALISATION
     Wire up badge count on page load.
     ════════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', function() {
    SC.cart._updateCartBadge();
  });

})();
